import {getMyCarDetail} from '@di-shared/api/cci/auth';
import {diApi} from '@di-shared/api/di';
import type {ApiResponseGetUbiResponse} from '@di-shared/api/di/__generated';
import {DEFAULT_CACHE_TIME, UNIT} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQueries} from '@tanstack/react-query';

export interface SafeDrivingSectionKrProps {
  vin: string;
}

function SafeDrivingSectionKr({vin}: SafeDrivingSectionKrProps) {
  const {t} = useInferableTranslation();
  const [{data: ubiData}, {data: carDetailData}] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApi.ubiController.getUbi4), vin],
        queryFn: () => diApi.ubiController.getUbi4({vin}),
        select: ({data}: ApiResponseGetUbiResponse) => data,
      },
      {
        queryKey: [getQueryKey(getMyCarDetail), vin],
        queryFn: () => getMyCarDetail(vin),
        staleTime: DEFAULT_CACHE_TIME.staleTime,
        gcTime: DEFAULT_CACHE_TIME.gcTime,
      },
    ],
  });

  return (
    <div className="section section_safe_score">
      <div className="section_container">
        <LinkTo
          to={ROUTE_PATHS.SAFE_DRIVING.getFullPath(vin)}
          className="section_container_full_btn"
          aria-label={t('T20087')}
          onClick={() =>
            emitEventLog({
              event: 'di_driveinfo_interaction',
              data_action: 'view score details',
            })
          }
        />
        <div className="section_container_header">
          <h3 className="body_large_b">{t('T20045')}</h3>
          <span
            className="ic ic_arrow_forward_16px"
            aria-hidden="true"
          ></span>
        </div>
        <div className="safe_score_info">
          <strong className="safe_score_title">
            <span className="safe_score_value">{ubiData.safetyDrvScore}</span>
            <span className="safe_score_unit">{t('T10056')}</span>
          </strong>
          <span className="safe_score_compare">
            <span className="score_compare_text">{t('T20052')}</span>
            <span className="score_compare_value">
              {t('T20006_$score', {
                score: ubiData.safetyDrvScoreDiff > 0 ? '+' + ubiData.safetyDrvScoreDiff : ubiData.safetyDrvScoreDiff,
              })}
            </span>
          </span>

          <div className="safe_score_percent">
            <div className="score_percent_item">
              <span className="score_percent_text">{t('T20053')}</span>
              <span className="score_percent_value">
                {ubiData.distribution}
                {UNIT.PERCENT}
              </span>
            </div>
            <div className="score_percent_item">
              <span className="score_percent_text">
                {carDetailData.vehicleModelName} {t('T20053')}
              </span>
              <span className="score_percent_value">
                {ubiData.modelDistribution}
                {UNIT.PERCENT}
              </span>
            </div>
          </div>
        </div>
        <div className="safe_score_summary">
          <ul className="score_summary_list">
            <li
              className="score_summary_item"
              role="text"
              aria-label={`${t('T10011')} ${t('T30145_$count', {count: ubiData.brstAccCount})}`}
            >
              <span
                className="ic ic_acceleration"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('T10011')}</span>
                <span className="score_summary_value">{t('T30145_$count', {count: ubiData.brstAccCount})}</span>
              </div>
            </li>
            <li
              className="score_summary_item"
              role="text"
              aria-label={`${t('T10012')} ${t('T30145_$count', {count: ubiData.brstDecCount})}`}
            >
              <span
                className="ic ic_deceleration"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('T10012')}</span>
                <span className="score_summary_value">{t('T30145_$count', {count: ubiData.brstDecCount})}</span>
              </div>
            </li>
            <li
              className="score_summary_item"
              role="text"
              aria-label={`${t('T10013')} ${t('T30145_$count', {count: ubiData.strtBrstAccCnt})}`}
            >
              <span
                className="ic ic_quick_start"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('T10013')}</span>
                <span className="score_summary_value">{t('T30145_$count', {count: ubiData.strtBrstAccCnt})}</span>
              </div>
            </li>
            <li
              className="score_summary_item"
              role="text"
              aria-label={`${t('T20000')} ${t('T30145_$count', {count: ubiData.nightDrvCount})}`}
            >
              <span
                className="ic ic_night_driving"
                aria-hidden="true"
              ></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('T20000')}</span>
                <span className="score_summary_value">{t('T30145_$count', {count: ubiData.nightDrvCount})}</span>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default SafeDrivingSectionKr;
